import { MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';

import Link from '@anwb/poncho/components/link';
import Typography from '@anwb/poncho/components/typography';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { isNewCarSelector, leaseCarUpgradesSelector } from 'store/selectors';
import { Upgrade } from 'types';
import { trackEvent } from 'utils/trackEvent';

import { getEquipmentModalABTest } from '../../../utils/getEquipmentModalABTest';

import { StandardEquipmentModal } from './StandardEquipmentModal';

import { UitrustingLinkContentContainer } from './styles/uitrustingLinkContent.styled';

type ModalKey = {
	upgradeId: number;
	open: boolean;
};

type UitrustingLinkProps = {
	upgrade: Upgrade;
};

export const UitrustingLink = ({ upgrade }: UitrustingLinkProps) => {
	const upgrades = useSelector(leaseCarUpgradesSelector);
	const isNewCar = useSelector(isNewCarSelector);

	const initialiseModalKeys = () => {
		let result: ModalKey[] = [];
		result = upgrades.map((a) => ({
			upgradeId: a.id,
			open: false,
		}));
		return result;
	};

	const equipmentModalABTest: string | false = getEquipmentModalABTest();
	const [modalKeys, setModalKeys] = useState<ModalKey[]>(initialiseModalKeys());
	const gaFunnelInfo = useGaFunnelInfo();
	const linkTitle = !isNewCar
		? 'Bekijk de gegevens en uitrusting'
		: upgrade.monthPrice
			? 'Bekijk de uitrusting'
			: 'Bekijk de standaarduitrusting';

	const openUitrustingModal = (upgradeId: number) =>
		modalKeys.find((key) => key.upgradeId === upgradeId)?.open;

	const removeKeyToBeUpdated = (upgradeId: number) =>
		modalKeys.filter((key) => key.upgradeId !== upgradeId);

	const eventField = upgrade.monthPrice ? 'uitrusting' : 'standaarduitrusting';

	const toggleUitrustingModal = (isOpen: boolean, upgradeId: number) => {
		if (isOpen) {
			trackEvent({
				event: 'user_interaction',
				type: 'form',
				component: 'pop-up',
				field: eventField,
				action: 'click',
				actionValue: 'open',
				...gaFunnelInfo,
			});
		}

		const temp = removeKeyToBeUpdated(upgradeId);
		temp.push({ upgradeId, open: isOpen });
		setModalKeys(temp);
	};

	const content = (
		<>
			{!isNewCar && equipmentModalABTest ? (
				<Link
					href="#"
					icon="new-modal"
					onClickCapture={(e: MouseEvent<HTMLElement>) => {
						e.preventDefault(); // stops event from bubbling up to parent FormOptionBlockItem
						toggleUitrustingModal(true, upgrade.id);
					}}
					dataTest={`uitvoeringModalLink_${upgrade.id}`}
				>
					{linkTitle}
				</Link>
			) : (
				<Typography
					href="#"
					variant="link-text"
					onClickCapture={(e: MouseEvent<HTMLElement>) => {
						e.preventDefault(); // stops event from bubbling up to parent FormOptionBlockItem
						toggleUitrustingModal(true, upgrade.id);
					}}
					dataTest={`uitvoeringModalLink_${upgrade.id}`}
				>
					{linkTitle}
				</Typography>
			)}

			<StandardEquipmentModal
				onOpen={(isOpen: boolean) => toggleUitrustingModal(isOpen, upgrade.id)}
				upgrade={upgrade}
				open={!!openUitrustingModal(upgrade.id)}
			/>
		</>
	);

	return !isNewCar ? (
		<UitrustingLinkContentContainer>{content}</UitrustingLinkContentContainer>
	) : (
		content
	);
};
